import { useState } from 'react';
import { ResponseException } from '../models';
import { AxiosPublic } from '../utils/axios';
import { AnalyticsItem } from '../interfaces/analyticsItem.interface';
import { ReviewParadigm, PermitLabels } from '@mvrb-frontend/shared-ui';
import { MetricesType } from '../pages/AnalyticsDashboard/ORSComments';

const ANALYTICS_BASE_URL = 'analytics';
const PUBLIC_REVIEWS_END_POINT = 'public-review';
const ORS_COMMENTS_END_POINT = 'ors-comments';
const PROCESSING_TIMES_END_POINT = 'processing-time';

export type PublicReviewQueryParams = {
  reviewTypes: string[];
  requestTypes: string[];
  board: string;
  activities: string[];
  proponents: Array<{ label: string; value: string }>;
  dateFrom: any;
  dateTo: any;
  checkboxFilters: any;
};

export type ProcessingTimeQueryParams = {
  reviewTypes: string[];
  requestType: string | null;
  boards: string[];
  proponents: Array<{ label: string; value: string }>;
  dateFrom: any;
  dateTo: any;
  checkboxFilters: any;
};

type ORSCommentsQueryParams = {
  lateComments?: boolean;
  reviewer?: string;
  metric?: MetricesType;
};

const sanitistsAnalyticsData = (data: AnalyticsItem[]): AnalyticsItem[] => {
  return (data || []).map(item => ({
    ...item,
    value: item.value ?? 0,
  }));
};

const useAnalyticsApi = () => {
  const [isFetchingPublicReviews, setFetchingPublicReviews] = useState(false);
  const [publicReviewsSuccess, setPublicReviewsSuccess] = useState(false);
  const [publicReviewsErrors, setPublicReviewsErrors] = useState<string[]>([]);
  const [fetchedPublicReviews, setFetchedPublicReviews] = useState<Array<AnalyticsItem>>([]);
  const [publicReviewsCount, setPublicReviewsCount] = useState<number | ''>('');

  const [isORSCommentsFetching, setORSCommentsFetching] = useState(false);
  const [orsCommentsSuccess, setORSCommentsSuccess] = useState(false);
  const [orsCommentsErrors, setORSCommentsErrors] = useState<string[]>([]);
  const [fetchedORSComments, setFetchedORSComments] = useState<Array<AnalyticsItem>>([]);
  const [orsCommentsTotal, setORSCommentsTotal] = useState<Array<AnalyticsItem>>([]);

  const [isProcessingTimesFetching, setProcessingTimesFetching] = useState(false);
  const [processingTimesSuccess, setProcessingTimesSuccess] = useState(false);
  const [processingTimesErrors, setProcessingTimesErrors] = useState<string[]>([]);
  const [fetchedProcessingTimes, setFetchedProcessingTimes] = useState<Array<AnalyticsItem>>([]);
  const [fetchedProcessingTimesWithHearings, setFetchedProcessingTimesWithHearings] = useState<Array<AnalyticsItem>>(
    [],
  );

  const fetchPublicReviews = async (query: PublicReviewQueryParams) => {
    try {
      const updatedQuery = {
        ...query,
        requestTypes: query.requestTypes?.map(r => (r === 'Policy/Guideline' ? 'Other' : r)),
        proponents: query.proponents?.map(el => el.value),
        dateFrom: query.dateFrom?.format().split('T')[0],
        dateTo: query.dateTo?.format().split('T')[0],
      };
      setFetchingPublicReviews(true);
      const { data } = await AxiosPublic.get(`/${ANALYTICS_BASE_URL}/${PUBLIC_REVIEWS_END_POINT}`, {
        params: {
          ...updatedQuery,
          board: updatedQuery.board === 'Any' ? '' : updatedQuery.board,
        },
      });

      const dataWitLabels = data?.result?.map(dataItem => {
        const formatName = name => {
          const trimmedName = name.trim();
          let paradigm = ReviewParadigm.find(paradigm => paradigm.value === trimmedName);
          if (!paradigm) {
            paradigm = PermitLabels.find(label => label.value === trimmedName);
          }
          return paradigm ? paradigm.label : trimmedName;
        };

        const formatString = str => {
          return str.split(/[,|-]/).map(formatName).join(', ');
        };

        return {
          ...dataItem,
          name: formatString(dataItem.name),
        };
      });
      setPublicReviewsCount(data?.filteredTotal);
      setFetchedPublicReviews(dataWitLabels);
      setPublicReviewsSuccess(true);
      setPublicReviewsErrors([]);
    } catch (e) {
      setPublicReviewsErrors(new ResponseException(e).getErrorMessage());
      setPublicReviewsSuccess(false);
      setFetchedPublicReviews([]);
      setPublicReviewsCount('');
    } finally {
      setFetchingPublicReviews(false);
    }
  };

  const fetchProcessingTimes = async (query: ProcessingTimeQueryParams) => {
    try {
      const updatedQuery = {
        ...query,
        proponents: query.proponents?.map(el => el.value),
        dateFrom: query.dateFrom?.format().split('T')[0],
        dateTo: query.dateTo?.format().split('T')[0],
      };
      setProcessingTimesFetching(true);
      const { data } = await AxiosPublic.get(`/${ANALYTICS_BASE_URL}/${PROCESSING_TIMES_END_POINT}`, {
        params: updatedQuery,
      });
      setFetchedProcessingTimes(sanitistsAnalyticsData(data?.standard || data));
      setFetchedProcessingTimesWithHearings(sanitistsAnalyticsData(data?.withHearings || []));
      setProcessingTimesSuccess(true);
      setProcessingTimesErrors([]);
    } catch (e) {
      setProcessingTimesErrors(new ResponseException(e).getErrorMessage());
      setProcessingTimesSuccess(false);
      setFetchedProcessingTimes([]);
      setFetchedProcessingTimesWithHearings([]);
    } finally {
      setProcessingTimesFetching(false);
    }
  };

  const fetchORSComments = async (query: ORSCommentsQueryParams) => {
    try {
      setORSCommentsFetching(true);
      const { data } = await AxiosPublic.get(`/${ANALYTICS_BASE_URL}/${ORS_COMMENTS_END_POINT}`, {
        params: query,
      });
      setFetchedORSComments(data?.filteredData);
      setORSCommentsSuccess(true);
      setORSCommentsErrors([]);
      setORSCommentsTotal(data?.totalMap);
    } catch (e) {
      setORSCommentsErrors(new ResponseException(e).getErrorMessage());
      setORSCommentsSuccess(false);
      setFetchedORSComments([]);
    } finally {
      setORSCommentsFetching(false);
    }
  };

  return {
    isFetchingPublicReviews,
    publicReviewsSuccess,
    publicReviewsErrors,
    fetchedPublicReviews,
    publicReviewsCount,
    fetchPublicReviews,
    isORSCommentsFetching,
    orsCommentsSuccess,
    orsCommentsErrors,
    fetchedORSComments,
    fetchORSComments,
    orsCommentsTotal,
    isProcessingTimesFetching,
    processingTimesSuccess,
    processingTimesErrors,
    fetchedProcessingTimes,
    fetchedProcessingTimesWithHearings,
    fetchProcessingTimes,
  };
};

export default useAnalyticsApi;
